import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
  Badge,
  ButtonDS,
  SendOutlined,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { roleDetails } from "../lib";
import { useCallback, useMemo, useState } from "react";
import { t } from "i18next";
import { ManagerCreationRole, ManagerDataDisplay } from "../usersAPI";
import "../../../../i18n/i18n";
import { Radio } from "@qivia/ui/src/designSystem/components/Radio";

export const AddUserRole = (props: {
  currentManager?: ManagerDataDisplay;
  onSubmit: (value: ManagerCreationRole) => void;
  onReturn: () => void;
  isUpdate?: boolean;
}) => {
  const { t } = useTranslation();

  const { onSubmit, onReturn } = props;
  const userRole: ManagerCreationRole[] = ["ADMIN", "USER", "DEPARTMENT_USER"];
  const [role, setRole] = useState<ManagerCreationRole | null>(null);

  const isCurrentRoleChosen = useMemo(() => {
    return (
      role && role !== "DEPARTMENT_USER" && props.currentManager?.role === role
    );
  }, [role, props.currentManager?.role]);

  const isDepartmentUser = useMemo(() => {
    return role === "DEPARTMENT_USER";
  }, [role]);

  const onSubmitRole = useCallback(() => {
    role && !isCurrentRoleChosen && onSubmit(role);
  }, [isCurrentRoleChosen, onSubmit, role]);

  return (
    <StyledModalContainer>
      <StyledModalContent>
        <Spacer y={1} />
        <StyledTitle>
          <TextCapitalized>{t(`users.modal.role.title`)}</TextCapitalized>
        </StyledTitle>
        <Spacer y={2} />
        {userRole.map((currentRole, index) => {
          return (
            <StyledAllWidth key={index + "role"}>
              <StyledRoleContainer>
                <Spacer x={1} />
                <StyledColumn>
                  <Spacer y={1.5} />
                  <TextCapitalized>
                    {t(`users.modal.role.title.${currentRole}`)}
                  </TextCapitalized>
                  <Spacer y={0.5} />
                  <Badge
                    text={t(`users.modal.role.badge.${currentRole}`)}
                    backgroundColor={
                      colors["colors/system/informative/informative_ultraLight"]
                    }
                    textColor={
                      colors["colors/system/informative/informative_normal"]
                    }
                  />
                  <Spacer y={1.5} />
                </StyledColumn>
                <Spacer x={3} />
                <div>
                  <Radio
                    isSelected={role === currentRole}
                    onClick={() => setRole(currentRole)}
                  />
                </div>
                <Spacer x={1} />
              </StyledRoleContainer>
              <Spacer y={0.5} />
            </StyledAllWidth>
          );
        })}
        <Spacer y={0.75} />
        {role && (
          <StyledAbsolute>
            <RoleInformations role={role} />
          </StyledAbsolute>
        )}
        <Spacer y={1.5} />
        <StyledButtons>
          <ButtonDS
            leftIcon={<ArrowBackOutlined />}
            text={t("users.modal.role.return")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={onReturn}
          />
          <Spacer x={1} />
          <ButtonDS
            rightIcon={
              isDepartmentUser ? <ArrowForwardOutlined /> : <SendOutlined />
            }
            text={
              isDepartmentUser
                ? t("users.modal.role.next")
                : props.isUpdate
                  ? t("users.modal.role.update")
                  : t("users.modal.role.submit")
            }
            format={"fill"}
            buttonType={"primary"}
            onClick={onSubmitRole}
            disabled={!!(!role || isCurrentRoleChosen)}
          />
        </StyledButtons>
        {isCurrentRoleChosen && (
          <>
            <Spacer y={1} />
            <StyledErrorMessage>
              <TextCapitalized>
                {t("users.modal.role.currentRoleChosen")}
              </TextCapitalized>
            </StyledErrorMessage>
          </>
        )}
      </StyledModalContent>
    </StyledModalContainer>
  );
};

const StyledErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors["colors/system/error/error_normal"]};
`;

const RoleInformations = (props: { role: ManagerCreationRole }) => {
  const details = roleDetails[props.role];
  return (
    <StyledDetails>
      {details.map((item, index) => (
        <div key={index + "details"}>
          <Spacer y={0.5} />
          <StyledFlex>
            <Spacer x={1} />
            <StyledIcon>{item.icon}</StyledIcon>
            <Spacer x={1} />
            <StyledText>
              <TextCapitalized>{t(item.text)}</TextCapitalized>
            </StyledText>
            <Spacer x={1} />
          </StyledFlex>
          <Spacer y={0.5} />
        </div>
      ))}
    </StyledDetails>
  );
};

const StyledAbsolute = styled.div`
  position: absolute;
  transform: translate(29rem, 4rem);
`;

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 19rem;
  border-radius: 0.5rem;
  background-color: ${colors["colors/surfaces/background/background_level2"]};
`;

const StyledAllWidth = styled.div`
  width: 100%;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledRoleContainer = styled.div`
  display: flex;
  align-items: center;
  border: 0.0625rem solid ${colors["colors/borders/button/primary"]};
  border-radius: 0.5rem;
  width: 100%;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StyledModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledModalContent = styled.div`
  flex-direction: column;
  display: flex;
  width: 33rem;
  align-items: center;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
`;

const StyledText = styled.div`
  color: ${colors["colors/text/darkGrey"]};
  ${typographies["Body/XXS"]}
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledIcon = styled.div`
  & svg {
    width: 1rem;
    height: 1rem;
  }
  fill: ${colors["colors/text/darkGrey"]};
`;
